import React, { useCallback, useEffect } from 'react'
import {
  selectCrossClientResultsPollingEnabled,
  selectLastCrossClientResultUpdatedAt,
  selectShouldPollCrossClientExecutionResults,
  setCrossClientResults,
  setCrossClientResultsExecutionPhase,
  setCrossClientResultsExecutionResultCount,
  startCrossClientExecutionPolling,
  stopCrossClientExecutionPolling,
} from '../../../store/crossClient/crossClientResults'
import { usePollingEffect } from '../../../apps/professional-app/fa/common/hooks/usePollingEffect'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useLazyGetAllCrossClientQueryExecutionResultsQuery, useLazyGetCrossClientQueryExecutionQuery } from '../../../store/crossClient/crossClient'

export interface CrossClientResultsPollerProps {
  executionId: string
}

export const CrossClientResultsPoller: React.FC<CrossClientResultsPollerProps> = ({ executionId }) => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const shouldPoll = useAppSelector(selectShouldPollCrossClientExecutionResults)
  const pollingEnabled = useAppSelector(selectCrossClientResultsPollingEnabled)
  const lastResultUpdatedAt = useAppSelector(selectLastCrossClientResultUpdatedAt)

  const [triggerGetCrossClientQueryExecution] = useLazyGetCrossClientQueryExecutionQuery()
  const [triggerGetAllCrossClientQueryResults] = useLazyGetAllCrossClientQueryExecutionResultsQuery()

  useEffect(() => {
    if (shouldPoll) {
      dispatch(startCrossClientExecutionPolling())
    } else {
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, shouldPoll])

  const getCrossClientExecution = useCallback(async () => {
    try {
      const execution = await triggerGetCrossClientQueryExecution({ accountId, executionId }).unwrap()
      dispatch(setCrossClientResultsExecutionPhase(execution.phase))
      dispatch(setCrossClientResultsExecutionResultCount(execution.resultCount))
    } catch (e) {
      console.error(e)
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, accountId, executionId, triggerGetCrossClientQueryExecution])

  const getCrossClientResults = useCallback(async () => {
    try {
      const results = await triggerGetAllCrossClientQueryResults({ accountId, executionId, updatedAfter: lastResultUpdatedAt }).unwrap()
      dispatch(setCrossClientResults(results.data))
    } catch (e) {
      console.error(e)
      dispatch(stopCrossClientExecutionPolling())
    }
  }, [dispatch, accountId, executionId, lastResultUpdatedAt, triggerGetAllCrossClientQueryResults])

  const { start: startCrossClientResultExecutionPolling, stop: stopCrossClientResultExecutionPolling } = usePollingEffect(getCrossClientExecution, [], {
    interval: 10000,
  })

  const { start: startCrossClientResultsPolling, stop: stopCrossClientResultsPolling } = usePollingEffect(getCrossClientResults, [], {
    interval: 3000,
  })

  useEffect(() => {
    if (pollingEnabled) {
      startCrossClientResultExecutionPolling()
      startCrossClientResultsPolling()
    } else {
      stopCrossClientResultExecutionPolling()
      stopCrossClientResultsPolling()
    }
  }, [
    pollingEnabled,
    startCrossClientResultsPolling,
    startCrossClientResultExecutionPolling,
    stopCrossClientResultsPolling,
    stopCrossClientResultExecutionPolling,
  ])

  return null
}
